import { useEffect, useRef, useState } from "react";

const ZoomableSVG = ({ imageUrl, width, height }) => {
  // zoom and image data
  const [clickedPositionBoundingBox, setClickedPositionBoundingBox] = useState(null); // zoomed in bounding box coordinates
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 }); // size of the image
  const [imageSrc, setImageSrc] = useState(null); // URL of the image
  
  // handling SVG interactions
  const svgRef = useRef(null); // SVG element used to access its dimensions
  const lastMousePos = useRef(null); // last mouse position
  const isMousePressed = useRef(false); // is mouse pressed? boolean

  // Get the image
  useEffect(() => {
    if (!imageUrl) return;

    // Fetch the image from the given URL
    fetch(imageUrl, { mode: "cors" })
    // src: https://developer.mozilla.org/en-US/docs/Web/API/Response/blob
    .then((response) => response.blob())
    .then((blob) => {
      const objectURL = URL.createObjectURL(blob);
      setImageSrc(objectURL);

      const img = new Image();
      img.src = objectURL;
      img.onload = () => {
        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      };
    })
    .catch((error) => console.error("Error fetching image:", error));
  }, [imageUrl]);

  // Handle mouse press (get dimensions and position in image)
  const handleMouseDown = (e) => {
    if (!svgRef.current) return;

    // get svg dimensions using ref
    const rect = svgRef.current.getBoundingClientRect();
    
    // Calculate mouse position in the image
    const mouseX = ((e.clientX - rect.left) / rect.width) * imageSize.width;
    const mouseY = ((e.clientY - rect.top) / rect.height) * imageSize.height;

    // ratio 1:1 pixel image / svg bounding box
    const viewWidth = Math.min(width, imageSize.width);
    const viewHeight = Math.min(height, imageSize.height);

    // Center the zoomed area around the mouse position
    const x = Math.max(0, Math.min(mouseX - viewWidth / 2, imageSize.width - viewWidth));
    const y = Math.max(0, Math.min(mouseY - viewHeight / 2, imageSize.height - viewHeight));

    // Set zoom in bounding box
    setClickedPositionBoundingBox({ x, y, width: viewWidth, height: viewHeight });

    // Update refs with mouse position and trigger zoom in
    lastMousePos.current = { x: mouseX, y: mouseY };
    isMousePressed.current = true;
  };

  // Handle mouse move
  const handleMouseMove = (e) => {
    if (!clickedPositionBoundingBox || !svgRef.current || !isMousePressed.current) return;

    // Get the new mouse position relative to the image
    const rect = svgRef.current.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * imageSize.width;
    const mouseY = ((e.clientY - rect.top) / rect.height) * imageSize.height;

    if (lastMousePos.current) {
      // Calculate movement difference
      const dx = mouseX - lastMousePos.current.x;
      const dy = mouseY - lastMousePos.current.y;

      // Calc bounding box for the new position
      setClickedPositionBoundingBox((prev) => {
        if (!prev) return null;

        // Clamp image to limits of the SVG cause we lose mouse position in image
        let newX = Math.max(0, Math.min(prev.x + dx, imageSize.width - prev.width));
        let newY = Math.max(0, Math.min(prev.y + dy, imageSize.height - prev.height));

        // Keeping width and height values and update position in x/y
        return { ...prev, x: newX, y: newY };
      });

      // Update mouse position
      lastMousePos.current = { x: mouseX, y: mouseY };
    }
  };

  // Reset refs to get the whole image back and cancel mouse tracking
  const handleMouseUp = () => {
    setClickedPositionBoundingBox(null);
    isMousePressed.current = false;
  };

  // Add mouse tracking
  useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []); 

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      viewBox={
        // Update view box either to see the whole image or the zoomed-in part using clickedPositionBoundingBox
        clickedPositionBoundingBox
          ? `${clickedPositionBoundingBox.x} ${clickedPositionBoundingBox.y} ${clickedPositionBoundingBox.width} ${clickedPositionBoundingBox.height}`
          : `0 0 ${imageSize.width} ${imageSize.height}`
      }
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      style={{ display: "block", cursor: clickedPositionBoundingBox ? "zoom-out" : "zoom-in", userSelect: "none" }}
    >
      {imageSrc && <image href={imageSrc} width={imageSize.width} height={imageSize.height} />}
    </svg>
  );
};

export default ZoomableSVG;